import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatDate = (dateStr) => {
  // Convert "2025-01-17" to "1/17/2025"
  const [year, month, day] = dateStr.split("-");
  return `${parseInt(month)}/${parseInt(day)}/${year}`;
};

const MetricsChart = ({
  isAdmin,
  epicCtr,
  calculatedCtr,
  clicksData,
  impressionsData,
  playsData,
}) => {
  const chartContainerRef = useRef(null);
  const [mapName, setMapName] = useState("");
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  const hasRequiredData = useMemo(() => {
    return clicksData && impressionsData && playsData;
  }, [clicksData, impressionsData, playsData]);

  const processData = useMemo(() => {
    if (!hasRequiredData) {
      return null;
    }

    // Create a map of all dates
    const dateMap = new Map();

    // Initialize with all dates from all sources
    [...clicksData, ...impressionsData, ...playsData].forEach((item) => {
      if (!dateMap.has(item.date)) {
        dateMap.set(item.date, {
          date: item.date,
          impressions: 0,
          clicks: 0,
          plays: 0,
          epicCtr: 0,
        });
      }
    });

    // Combine data for each date
    clicksData.forEach((item) => {
      if (dateMap.has(item.date)) {
        dateMap.get(item.date).clicks = item.value;
      }
    });

    impressionsData.forEach((item) => {
      if (dateMap.has(item.date)) {
        dateMap.get(item.date).impressions = item.value;
      }
    });

    playsData.forEach((item) => {
      if (dateMap.has(item.date)) {
        dateMap.get(item.date).plays = item.value;
      }
    });

    // If EPIC CTR data is available, use it directly
    if (epicCtr) {
      epicCtr.forEach((item) => {
        if (dateMap.has(item.date)) {
          dateMap.get(item.date).epicCtr = item.value;
        }
      });
    }

    // Filter dates that have data from all sources and sort by date
    return Array.from(dateMap.values())
      .filter(
        (item) => item.clicks > 0 || item.impressions > 0 || item.plays > 0
      )
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((item) => ({
        ...item,
        formattedDate: formatDate(item.date),
        // EPIC CTR from the provided data
        epicCtr: item.epicCtr,
        // Calculate CTR from clicks and impressions
        calculatedCtr:
          item.impressions > 0
            ? ((item.clicks / item.impressions) * 100).toFixed(2)
            : "0.00",
        // CTP is now using calculated CTR and plays
        ctp:
          item.clicks > 0
            ? ((item.plays / item.clicks) * 100).toFixed(2)
            : "0.00",
      }));
  }, [epicCtr, clicksData, impressionsData, playsData, hasRequiredData]);

  const chartData = useMemo(() => {
    if (!processData) return null;

    return {
      labels: processData.map((item) => item.formattedDate),
      datasets: [
        {
          label: "EPIC CTR (%)",
          data: processData.map((item) => parseFloat(item.epicCtr) || 0),
          borderColor: "#10B981",
          backgroundColor: "#10B981",
          tension: 0.1,
        },
        {
          label: "Calc CTR (%)",
          data: processData.map((item) => parseFloat(item.calculatedCtr)),
          borderColor: "#EF4444",
          backgroundColor: "#EF4444",
          tension: 0.1,
        },
        {
          label: "CTP (%)",
          data: processData.map((item) => parseFloat(item.ctp)),
          borderColor: "#F59E0B",
          backgroundColor: "#F59E0B",
          tension: 0.1,
        },
        {
          label: "Clicks",
          data: processData.map((item) => item.clicks),
          borderColor: "#3B82F6",
          backgroundColor: "#3B82F6",
          tension: 0.1,
          yAxisID: "y1",
        },
        {
          label: "Impressions",
          data: processData.map((item) => item.impressions),
          borderColor: "#EC4899",
          backgroundColor: "#EC4899",
          tension: 0.1,
          yAxisID: "y1",
        },
        {
          label: "Plays",
          data: processData.map((item) => item.plays),
          borderColor: "#8B5CF6",
          backgroundColor: "#8B5CF6",
          tension: 0.1,
          yAxisID: "y1",
        },
      ],
    };
  }, [processData]);

  const handleSaveClick = () => {
    setShowNamePrompt(true);
  };

  const handleSaveImage = async (e) => {
    e?.preventDefault(); // Handle form submission
    if (!chartContainerRef.current || !mapName.trim()) return;

    try {
      const canvas = await html2canvas(chartContainerRef.current, {
        backgroundColor: "#1F2937",
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const link = document.createElement("a");
      const date = new Date().toISOString().split("T")[0];
      link.download = `${mapName.trim()}-metrics-${date}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      // Reset after saving
      setShowNamePrompt(false);
      setMapName("");
    } catch (error) {
      console.error("Error saving image:", error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="text-sm text-gray-400">
          Data Status:
          <ul className="list-disc pl-5 mt-1">
            <li className={clicksData ? "text-green-500" : "text-gray-500"}>
              Clicks: {clicksData ? "Loaded" : "Waiting"}
            </li>
            <li
              className={impressionsData ? "text-green-500" : "text-gray-500"}
            >
              Impressions: {impressionsData ? "Loaded" : "Waiting"}
            </li>
            <li className={playsData ? "text-green-500" : "text-gray-500"}>
              Plays: {playsData ? "Loaded" : "Waiting"}
            </li>
            <li className={epicCtr ? "text-green-500" : "text-gray-500"}>
              EPIC CTR: {epicCtr ? "Loaded" : "Not Available"}
            </li>
            <li className="text-green-500">
              Calc CTR: Auto-calculated from clicks/impressions
            </li>
            <li className="text-green-500">
              CTP: Auto-calculated from plays/clicks
            </li>
          </ul>
        </div>
      </div>

      {showNamePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <form onSubmit={handleSaveImage} className="space-y-4">
              <h3 className="text-lg font-medium text-gray-200">
                Enter Map Name
              </h3>
              <input
                type="text"
                value={mapName}
                onChange={(e) => setMapName(e.target.value)}
                placeholder="Enter map name..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowNamePrompt(false);
                    setMapName("");
                  }}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!mapName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {chartData ? (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={handleSaveClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Save as Image
            </button>
          </div>

          <div
            ref={chartContainerRef}
            className="space-y-6 bg-gray-800 p-6 rounded-lg"
          >
            <Line
              data={chartData}
              options={{
                responsive: true,
                interaction: {
                  mode: "index",
                  intersect: false,
                },
                plugins: {
                  legend: {
                    position: "top",
                    labels: {
                      color: "#E5E7EB",
                      font: {
                        size: 12,
                      },
                      usePointStyle: true,
                      padding: 15,
                    },
                    onClick: function (e, legendItem, legend) {
                      const index = legendItem.datasetIndex;
                      const ci = legend.chart;
                      if (ci.isDatasetVisible(index)) {
                        ci.hide(index);
                        legendItem.hidden = true;
                      } else {
                        ci.show(index);
                        legendItem.hidden = false;
                      }
                    },
                  },
                  title: {
                    display: true,
                    text: "Conversion Metrics Analysis",
                    color: "#E5E7EB",
                    font: {
                      size: 16,
                      weight: "bold",
                    },
                  },
                  tooltip: {
                    backgroundColor: "#374151",
                    titleColor: "#E5E7EB",
                    bodyColor: "#E5E7EB",
                    padding: 12,
                    cornerRadius: 4,
                  },
                },
                scales: {
                  y: {
                    type: "linear",
                    display: true,
                    position: "left",
                    title: {
                      display: true,
                      text: "Percentage (%)",
                      color: "#E5E7EB",
                    },
                    grid: {
                      color: "rgba(229, 231, 235, 0.1)",
                    },
                    ticks: {
                      color: "#E5E7EB",
                      font: {
                        size: 11,
                      },
                    },
                  },
                  y1: {
                    type: "linear",
                    display: true,
                    position: "right",
                    title: {
                      display: true,
                      text: "Count",
                      color: "#E5E7EB",
                    },
                    grid: {
                      drawOnChartArea: false,
                    },
                    ticks: {
                      color: "#E5E7EB",
                      font: {
                        size: 11,
                      },
                    },
                  },
                  x: {
                    grid: {
                      color: "rgba(229, 231, 235, 0.1)",
                    },
                    ticks: {
                      color: "#E5E7EB",
                      font: {
                        size: 11,
                      },
                    },
                  },
                },
                animation: {
                  duration: 0, // Disable animations for better image capture
                },
              }}
            />

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Impressions
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Clicks
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Plays
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      EPIC CTR (%)
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      Calc CTR (%)
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                    >
                      CTP (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {processData.map((row, index) => (
                    <tr
                      key={row.date}
                      className={
                        index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.formattedDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.impressions.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.clicks.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.plays.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.epicCtr}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.calculatedCtr}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {row.ctp}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-900">
                  <tr>
                    <td className="px-6 py-3 text-sm font-medium text-gray-300">
                      Totals
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-300">
                      {processData
                        .reduce((sum, row) => sum + row.impressions, 0)
                        .toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-300">
                      {processData
                        .reduce((sum, row) => sum + row.clicks, 0)
                        .toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-300">
                      {processData
                        .reduce((sum, row) => sum + row.plays, 0)
                        .toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-300">
                      {(
                        processData.reduce(
                          (sum, row) => sum + parseFloat(row.epicCtr),
                          0
                        ) / processData.length
                      ).toFixed(2)}
                      %
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-300">
                      {(
                        processData.reduce(
                          (sum, row) => sum + parseFloat(row.calculatedCtr),
                          0
                        ) / processData.length
                      ).toFixed(2)}
                      %
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-300">
                      {(
                        processData.reduce(
                          (sum, row) => sum + parseFloat(row.ctp),
                          0
                        ) / processData.length
                      ).toFixed(2)}
                      %
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="text-lg font-medium">No Data Available</h3>
            <p className="mt-2">
              Please upload at least Clicks, Impressions, and Plays data files
              to view conversion metrics.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsChart;
